import React, { useState, useMemo } from 'react';
import { Search, Grid, List, X } from 'lucide-react';
import TemplateCard from './TemplateCard';
import TemplatePreviewModal from './TemplatePreviewModal';

/**
 * Flat, Minimal Template Selector
 * Colors: #D45614 (primary) and white only
 */
const TemplateSelector = ({ templates, selectedTemplate, onSelect }) => {
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedStyle, setSelectedStyle] = useState('all');
    const [viewMode, setViewMode] = useState('grid');
    const [sortBy, setSortBy] = useState('popular');

    // Style categories for filtering
    const styleCategories = [
        { value: 'all', label: 'All' },
        { value: 'minimalist', label: 'Minimalist', keywords: ['minimalist', 'clean', 'simple'] },
        { value: 'luxury', label: 'Luxury', keywords: ['prestige', 'luxury', 'premium', 'elegant'] },
        { value: 'modern', label: 'Modern', keywords: ['modern', 'contemporary', 'dynamic'] },
        { value: 'creative', label: 'Creative', keywords: ['artistic', 'creative', 'studio', 'craft'] },
        { value: 'bold', label: 'Bold', keywords: ['impact', 'bold', 'vintage', 'vogue'] }
    ];

    const popularTemplateIds = [10, 11, 14, 15, 17];

    const filteredTemplates = useMemo(() => {
        let filtered = templates.filter(template => {
            const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                template.features.some(f => f.toLowerCase().includes(searchQuery.toLowerCase()));

            if (!matchesSearch) return false;

            if (selectedStyle === 'all') return true;

            const styleCategory = styleCategories.find(s => s.value === selectedStyle);
            if (!styleCategory?.keywords) return true;

            return styleCategory.keywords.some(keyword =>
                template.name.toLowerCase().includes(keyword) ||
                template.description.toLowerCase().includes(keyword)
            );
        });

        switch (sortBy) {
            case 'popular':
                filtered.sort((a, b) => {
                    const aPopular = popularTemplateIds.includes(a.id);
                    const bPopular = popularTemplateIds.includes(b.id);
                    if (aPopular && !bPopular) return -1;
                    if (!aPopular && bPopular) return 1;
                    return a.id - b.id;
                });
                break;
            case 'name':
                filtered.sort((a, b) => a.name.localeCompare(b.name));
                break;
            case 'sections':
                filtered.sort((a, b) => b.sections.length - a.sections.length);
                break;
            default:
                break;
        }

        return filtered;
    }, [templates, searchQuery, selectedStyle, sortBy]);

    return (
        <div className="min-h-screen bg-white">
            <div className="max-w-6xl mx-auto px-6 py-8">
                {/* Header - Simple */}
                <div className="mb-8">
                    <h1 className="text-2xl font-semibold text-gray-900 mb-2">
                        Choose Your Template
                    </h1>
                    <p className="text-gray-500">
                        {templates.length} templates available
                    </p>
                </div>

                {/* Search & Filters - Flat design */}
                <div className="mb-8 pb-6 border-b border-gray-100">
                    {/* Search Bar - Minimal */}
                    <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                            type="text"
                            placeholder="Search templates..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-10 py-2.5 border-b border-gray-200 focus:border-[#D45614] focus:outline-none text-sm bg-transparent"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>

                    {/* Filter Row - Flat */}
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        {/* Style Filters - Flat buttons */}
                        <div className="flex items-center gap-1 flex-wrap">
                            {styleCategories.map(style => (
                                <button
                                    key={style.value}
                                    onClick={() => setSelectedStyle(style.value)}
                                    className={`px-3 py-1.5 text-sm font-medium transition-all ${
                                        selectedStyle === style.value
                                            ? 'text-[#D45614] border-b-2 border-[#D45614]'
                                            : 'text-gray-500 hover:text-gray-900'
                                    }`}
                                >
                                    {style.label}
                                </button>
                            ))}
                        </div>

                        {/* Sort & View - Flat */}
                        <div className="flex items-center gap-4">
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="px-2 py-1.5 text-sm text-gray-600 focus:outline-none bg-transparent cursor-pointer"
                            >
                                <option value="popular">Popular</option>
                                <option value="name">Name</option>
                                <option value="sections">Features</option>
                            </select>

                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setViewMode('grid')}
                                    className={`p-1.5 transition-all ${
                                        viewMode === 'grid' ? 'text-[#D45614]' : 'text-gray-400'
                                    }`}
                                >
                                    <Grid className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => setViewMode('list')}
                                    className={`p-1.5 transition-all ${
                                        viewMode === 'list' ? 'text-[#D45614]' : 'text-gray-400'
                                    }`}
                                >
                                    <List className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Results Count - Minimal */}
                <div className="flex items-center justify-between mb-6">
                    <p className="text-sm text-gray-500">
                        {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''}
                        {searchQuery && <span className="text-[#D45614]"> for "{searchQuery}"</span>}
                    </p>
                    {(searchQuery || selectedStyle !== 'all') && (
                        <button
                            onClick={() => {
                                setSearchQuery('');
                                setSelectedStyle('all');
                            }}
                            className="text-sm text-[#D45614] hover:underline"
                        >
                            Clear filters
                        </button>
                    )}
                </div>

                {/* Templates Grid - Flat */}
                {filteredTemplates.length > 0 ? (
                    <div className={viewMode === 'grid'
                        ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'
                        : 'divide-y divide-gray-100'
                    }>
                        {filteredTemplates.map(template => (
                            <TemplateCard
                                key={template.id}
                                template={template}
                                isSelected={selectedTemplate?.id === template.id}
                                onSelect={onSelect}
                                onPreview={(t) => setPreviewTemplate({ no: t.id, name: t.name })}
                                isPopular={popularTemplateIds.includes(template.id)}
                                viewMode={viewMode}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-16">
                        <Search className="w-10 h-10 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-base font-medium text-gray-900 mb-2">No templates found</h3>
                        <p className="text-sm text-gray-500 mb-4">Try adjusting your search or filters</p>
                        <button
                            onClick={() => {
                                setSearchQuery('');
                                setSelectedStyle('all');
                            }}
                            className="px-4 py-2 bg-[#D45614] text-white text-sm font-medium hover:bg-[#c04d12] transition-colors"
                        >
                            Clear Filters
                        </button>
                    </div>
                )}

                {/* Template Preview Modal */}
                {previewTemplate && (
                    <TemplatePreviewModal
                        templateNo={previewTemplate.no}
                        templateName={previewTemplate.name}
                        onClose={() => setPreviewTemplate(null)}
                        onSelect={(no) => {
                            setPreviewTemplate(null);
                            const template = templates.find(t => t.id === no);
                            if (template) onSelect(template);
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default TemplateSelector;
